/**
 * "Load more" for the article list. Fetches the next page of the same list, appends its article cards to the current
 * grid and updates (or removes) the button. Without JavaScript the button is a normal link to the next page.
 *
 * Markup contract: the element wraps one `a[data-load-more]`; the grid is the `[data-article-grid]` element of the same
 * `.article-list` section; every card inside the grid is a direct child.
 */
class ArticleLoadMore extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-load-more]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    this.#loadNext(link);
  };

  /** @param {HTMLAnchorElement} link */
  async #loadNext(link) {
    if (this.getAttribute('aria-busy') === 'true') return;
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(link.href);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      const grid = this.closest('.article-list')?.querySelector('[data-article-grid]');
      const nextGrid = doc.querySelector('.article-list [data-article-grid]');
      if (!grid || !nextGrid) throw new Error('Article grid not found');

      const firstNew = nextGrid.firstElementChild;
      grid.append(...Array.from(nextGrid.children).map((card) => document.importNode(card, true)));

      const nextLink = doc.querySelector('.article-list [data-load-more]');
      if (nextLink instanceof HTMLAnchorElement) {
        link.href = nextLink.getAttribute('href') || link.href;
      } else {
        this.remove();
      }

      const focusTarget = firstNew ? grid.querySelector(`a[href="${firstNew.querySelector('a')?.getAttribute('href')}"]`) : null;
      if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    } catch {
      window.location.href = link.href;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('article-load-more')) customElements.define('article-load-more', ArticleLoadMore);
